<template>
  <div class="product-modal-creators">
    <div class="report-header">
      <div class="left">
        <div class="title">Creators ({{ data_sum ? data_sum.row_count : '' }})</div>
        <FilterDropdown :params="params" :disabled="is_empty_data" @update-filter="updateFilter" />
      </div>
      <div class="ui-search">
        <Search @onenter="search" :disabled="is_empty_data" />
      </div>
      <div class="right">
        <Dropdown :items="select_pagenum" :disabled="is_empty_data" @update-dropdown="updatePagenum" />
        <Pagenavi :disabled="is_empty_data" :page_total="page_total" :page_current="page_current" @goto-page="gotoPage" />
      </div>
    </div>

    <FilterResults v-show="has_filters" :params="params" @update-filter="updateFilter" />

    <Loading class="static" v-if="is_loading" />
    <Scrollbar class="data-list" v-else :show_bar="true">
      <Table :class="{ 'is-loading': is_loading_data }">
        <template #head>
          <tr>
            <TableTh name="name" title="Name" :sort_value="sortValue" @sort="handleSort" content="Name" />
            <TableTh
              name="anchor_status"
              title="Product Status"
              :sort_value="sortValue"
              @sort="handleSort"
              content="Current status of each creator's storefront with the product."
            />
            <th><span>Updates</span></th>
            <TableTh
              v-if="!is_link_only"
              name="product_views"
              title="Product Views"
              :sort_value="sortValue"
              @sort="handleSort"
              content="Cumulative product views for the time period selected."
            />
            <TableTh
              name="click"
              title="Product Clicks"
              :sort_value="sortValue"
              @sort="handleSort"
              content="Total product link clicks for the time period selected."
            />
            <TableTh
              name="sales"
              title="Sales"
              :sort_value="sortValue"
              @sort="handleSort"
              content="Sales driven by the storefront for the time period selected."
            />
            <TableTh
              name="sales_value"
              title="Sales Value"
              :sort_value="sortValue"
              @sort="handleSort"
              content="Gross transaction value of sales driven by storefront."
            />
            <TableTh
              name="earnings"
              title="Earnings"
              :sort_value="sortValue"
              @sort="handleSort"
              content="Earnings for the time period selected."
            />
          </tr>
        </template>
        <template #body>
          <template v-if="!is_loading && data.length === 0">
            <tr>
              <td :colspan="is_link_only ? 7 : 8"><NoData /></td>
            </tr>
          </template>
          <tr
            v-else
            v-for="(row, index) in data"
            :key="index"
            class="tbody-tr"
            @click.stop="emit('go-profile', row)"
          >
            <td class="name">
              <div class="profile">
                <img
                  v-if="row.userProfile"
                  :src="row.userProfile.avatar_full_url"
                />
                <img v-else src="@/assets/images/user.png"/>
                <div class="name-text-box">
                  <strong class="text-ellipsis" v-html="row.first_name + ' ' + row.last_name"></strong>
                </div>
              </div>
            </td>
            <td class="status-cell">
              <div class="status-tags">
                <span
                  v-if="row.anchor_page_status !== null && row.anchor_page_status !== undefined"
                  :class="['status-tag', 'class-' + row.anchor_page_status]"
                >
                  <i class="status-dot"></i>{{ row.anchor_page_status_text }}
                </span>
                <span v-else class="status-na">N/A</span>
                <span v-if="row.is_featured" class="badge badge-featured">Featured</span>
                <span v-if="row.is_removed" class="badge badge-removed">Removed</span>
                <span v-if="row.is_deleted" class="badge badge-deleted">Deleted</span>
              </div>
            </td>
            <td class="updates-cell">
              <div v-if="row.store_front_created_at" class="update-row">
                <span class="label">Added:</span> {{ $filters.momentFormat(row.store_front_created_at, 'll') }}
              </div>
              <div v-if="row.published_at" class="update-row">
                <span class="label">Published:</span> {{ $filters.momentFormat(row.published_at, 'll') }}
              </div>
              <div v-if="row.last_store_front_update_at" class="update-row">
                <span class="label">Last updated:</span> {{ $filters.momentFormat(row.last_store_front_update_at, 'll') }}
              </div>
              <div v-if="row.disabled_at && row.anchor_page_status === 0" class="update-row">
                <span class="label">Disabled:</span> {{ $filters.momentFormat(row.disabled_at, 'll') }}
              </div>
            </td>
            <td v-if="!is_link_only">
              <span v-if="row.product_views != null && !isNaN(row.product_views)">
                {{ Number(row.product_views).toLocaleString('en-US') }}
              </span>
              <span v-else>---</span>
            </td>
            <td>
              <div class="td-flex">
                <span v-if="row.click != null && !isNaN(row.click)">
                  {{ Number(row.click).toLocaleString('en-US') }}
                </span>
                <span v-else>---</span>
                <i class="lig" v-if="store.state.save_sortlink_data === 'all' && row.click_include_shortlink_data">
                  <svg-icon name="lightning" />
                </i>
              </div>
            </td>
            <td>
              <div class="td-flex">
                <span v-if="row.sales != null && !isNaN(row.sales)">
                  {{ Number(row.sales).toLocaleString('en-US') }}
                </span>
                <span v-else>---</span>
                <i class="lig" v-if="store.state.save_sortlink_data === 'all' && row.sales_include_shortlink_data">
                  <svg-icon name="lightning" />
                </i>
              </div>
            </td>
            <td>
              <div class="td-flex">
                <span v-if="!isNaN(row.sales_value)">
                  ${{ Number(row.sales_value).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) }}
                </span>
                <span v-else>---</span>
                <i class="lig" v-if="store.state.save_sortlink_data === 'all' && row.sale_value_include_shortlink_data">
                  <svg-icon name="lightning" />
                </i>
              </div>
            </td>
            <td>
              <span v-if="row.earnings != null && !isNaN(row.earnings)">
                ${{ Number(row.earnings).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) }}
              </span>
              <span v-else>---</span>
            </td>
          </tr>
        </template>
        <template #foot v-if="data_sum && data_sum.row_count > 0 && search_keyword === '' && !has_filters">
          <tr>
            <td colspan="2">Total {{ data_sum.row_count }}</td>
            <td></td>
            <td v-if="!is_link_only">{{ formatNumber(data_sum.product_view_total) }}</td>
            <td>{{ formatNumber(data_sum.clicks_total) }}</td>
            <td>{{ formatNumber(data_sum.sales_total) }}</td>
            <td>
              ${{ Number(data_sum.sales_value_total).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) }}
            </td>
            <td>${{ Number(data_sum.earnings_total).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) }}</td>
          </tr>
        </template>
      </Table>
    </Scrollbar>
  </div>
</template>

<script setup>
import { ref, reactive, computed, watch, onMounted, getCurrentInstance } from 'vue';
import defaultAvatar from '@/assets/images/user.png';
import { useStore } from 'vuex';
import SvgIcon from '@/components/ui/SvgIcon.vue';
import Loading from '@/components/ui/Loading.vue';
import Table from '@/components/ui/Table.vue';
import TableTh from '@/components/ui/TableTh.vue';
import Scrollbar from '@/components/ui/Scrollbar.vue';
import NoData from '@/components/creator/NoData.vue';
import Search from '@/components/ui/Search.vue';
import Dropdown from '@/components/ui/Dropdown.vue';
import FilterDropdown from '@/components/FilterDropdown.vue';
import FilterResults from '@/components/FilterResults.vue';
import Pagenavi from '@/components/ui/Pagenavi.vue';
import { dataSourceToApi } from '@/utils/SwitchFun.js';

const props = defineProps({
  product_id: { type: [String, Number], required: true },
  start_date: { type: String, required: true },
  end_date: { type: String, required: true },
  sortlink_data: { type: String, default: 'all' },
  curated_check: { type: Boolean, default: false },
  is_link_only: { type: Boolean, default: false },
});

const emit = defineEmits(['select-tab', 'go-profile']);

const store = useStore();
const { proxy } = getCurrentInstance();
const $ajax = proxy.$ajax;

const data = ref([]);
const data_sum = ref(null);
const sort_key = ref('product_views');
const sort_val = ref('desc');
const search_keyword = ref('');
const page_size = ref(50);
const page_total = ref(0);
const page_current = ref(1);
const is_loading = ref(true);
const is_loading_data = ref(false);
const is_empty_data = ref(false);
const has_filters = ref(false);
const select_pagenum = [
  { id: 50, text: '50/page' },
  { id: 20, text: '20/page' },
];
const params = reactive({
  profile_status: 0,
  storefront_status: 0,
  recently_added: 0,
  metric: 0,
  metric_type: 0,
  metric_amount: '',
  metric_min_amount: '',
  metric_max_amount: '',
  social: {
    metric: 1,
    metric_msg: '',
    metric_min_amount_input: '',
    metric_max_amount_input: '',
  },
});

const sortValue = computed(() => ({ sort_key: sort_key.value, sort_val: sort_val.value }));

const formatNumber = (num) => (num ? num.toLocaleString('en-US') : '---');

const getApiParamStr = (api) => {
  if (search_keyword.value) api = `${api}&keyword=${search_keyword.value}`;
  api = `${api}&per_page=${page_size.value}&page=${page_current.value}&start_date=${props.start_date}&end_date=${props.end_date}`;

  if (store.state.save_selected_tags?.length > 0) {
    store.state.save_selected_tags.forEach((tag) => { api += `&creator_tag_ids[]=${tag.id}`; });
  }
  if (store.state.save_tag_operation_type) {
    api += `&operation_type=${store.state.save_tag_operation_type}`;
  }
  // no operation_type for tiers: a creator sits in exactly one tier, so it is always Or
  if (store.state.save_selected_tiers?.length > 0) {
    store.state.save_selected_tiers.forEach((tier) => { api += `&tier_ids[]=${tier.id}`; });
  }

  const filters = [];
  if (params.profile_status === 1) filters.push({ field_name: 'creator_status', value: 'joined' });
  if (params.profile_status === 2) filters.push({ field_name: 'creator_status', value: 'pending' });
  if (params.profile_status === 3) filters.push({ field_name: 'creator_status', value: 'deleted' });
  if (params.storefront_status === 1) filters.push({ field_name: 'anchor_status', value: 'active' });
  if (params.storefront_status === 2) filters.push({ field_name: 'anchor_status', value: 'pending' });
  if (params.storefront_status === 3) filters.push({ field_name: 'anchor_status', value: 'disabled' });
  if (params.storefront_status === 4) filters.push({ field_name: 'anchor_status', value: 'declined' });
  if (params.storefront_status === 5) filters.push({ field_name: 'anchor_status', value: 'invited' });
  if (params.recently_added === 1) filters.push({ field_name: 'recently_added', value: 'recently_added' });
  if (params.metric_amount !== '' || (params.metric_min_amount !== '' && params.metric_max_amount !== '')) {
    const metric_map = { 0: 'product_views', 3: 'click', 2: 'sales', 1: 'sales_value' };
    const metric_txt = metric_map[params.metric] || '';
    if (params.metric_type === 0) filters.push({ field_name: metric_txt, operator: '>', value1: params.metric_amount });
    else if (params.metric_type === 1) filters.push({ field_name: metric_txt, operator: '<', value1: params.metric_amount });
    else filters.push({ field_name: metric_txt, value1: params.metric_min_amount, value2: params.metric_max_amount });
  }
  if (params.social.metric_max_amount_input && params.social.metric_min_amount_input) {
    const whi = params.social.metric === 1 ? 'follower_filter_tikTok' : 'follower_filter_instagram';
    filters.push({ field_name: whi, value1: params.social.metric_min_amount_input, value2: params.social.metric_max_amount_input });
  }

  if (filters.length > 0) {
    has_filters.value = true;
    api = `${api}&filters=${JSON.stringify(filters)}`;
  } else {
    has_filters.value = false;
  }
  return api;
};

const getTable = () => {
  if (!is_loading.value) is_loading_data.value = true;
  let api = `/analytics/product-performance-by-creators?product_id=${props.product_id}&is_include_shortlink_data=${dataSourceToApi(props.sortlink_data)}&is_include_curation_data=${props.curated_check ? '1' : '0'}`;
  if (sort_key.value) api = `${api}&sort_key=${sort_key.value}&sort_val=${sort_val.value}`;
  api = getApiParamStr(api);

  $ajax(api, { method: 'get', withCredentials: true, data: {} }).then((res) => {
    data.value = res.data.data.data.map((e) => {
      let show_status_time = '';
      switch (e.anchor_page_status) {
        case 100: show_status_time = e.accept_store_front_datetime; break;
        case 0: show_status_time = e.disabled_at; break;
        case -10: show_status_time = e.declined_at; break;
        case 10: show_status_time = e.unavailable_at; break;
        case 20: show_status_time = e.created_at; break;
      }
      return { ...e, show_status_time };
    });
    page_total.value = res.data.data.last_page;
    page_current.value = res.data.data.current_page;
    const has_params = params.profile_status !== 0 || params.storefront_status !== 0 || params.recently_added !== 0 ||
      ((params.metric_type === 0 || params.metric_type === 1) && params.metric_amount !== '') ||
      (params.metric_type === 2 && params.metric_min_amount !== '' && params.metric_max_amount !== '');
    is_empty_data.value = !is_loading.value && data.value.length === 0 && search_keyword.value === '' && !has_params;
    is_loading.value = false;
    is_loading_data.value = false;
  });
};

const getSum = () => {
  let api = `/analytics/product-performance-by-creators-sum?product_id=${props.product_id}&is_include_shortlink_data=${dataSourceToApi(props.sortlink_data)}&is_include_curation_data=${props.curated_check ? '1' : '0'}`;
  api = getApiParamStr(api);
  $ajax(api, { method: 'get', withCredentials: true }).then((res) => {
    data_sum.value = res.data.data;
  });
};

const fetchAll = () => {
  getTable();
  getSum();
};

const handleSort = (key) => {
  if (key === sort_key.value) {
    sort_val.value = sort_val.value === 'asc' ? 'desc' : 'asc';
  } else {
    sort_key.value = key;
    sort_val.value = 'desc';
  }
  fetchAll();
};

const search = (keyword) => {
  sort_key.value = '';
  sort_val.value = 'desc';
  search_keyword.value = keyword;
  fetchAll();
};

const gotoPage = (page) => {
  page_current.value = page;
  getTable();
};

const updatePagenum = (obj) => {
  page_size.value = obj.id;
  getTable();
};

const updateFilter = (obj) => {
  Object.assign(params, obj);
  fetchAll();
};

watch(
  () => [props.start_date, props.end_date, props.sortlink_data, props.curated_check],
  () => {
    page_current.value = 1;
    fetchAll();
  }
);

onMounted(() => {
  sort_key.value = props.is_link_only ? 'click' : 'product_views';
  fetchAll();
});
</script>

<style lang="scss">
.product-modal-creators {
  .report-header {
    display: flex;
    align-items: center;
    padding: 16px 0;
    position: sticky;
    top: 90px;
    background: var(--bg-01);
    z-index: 10;

    .ui-search {
      flex: 1;
      padding: 0 10px;

      .ui-search-input .input {
        max-width: unset;
      }
    }

    .left {
      display: flex;
      align-items: center;

      .title {
        font: var(--font-demi-20);
        color: var(--black-90);
      }

      .ui-filter-dropdown {
        margin-left: 10px;
      }
    }

    .right {
      display: flex;
      align-items: center;

      .ui-dropdown {
        margin-right: 10px;
      }
    }
  }

  .filter-results{
    margin-top: 0;
  }

  .data-list {
    height: calc(98vh - 220px) !important;
    padding-bottom: 0px;
  }

  .ui-table {
    thead{
      top: 0;
    }
    thead tr {
      border-top: 1px solid var(--black-20);
    }

    .tbody-tr {
      cursor: pointer;
    }
    tfoot{
      bottom: -1px;
    }
  }

  .status-cell {
    .status-tags {
      display: flex;
      align-items: center;
      flex-wrap: wrap;
      gap: 4px;
    }

    .status-tag {
      display: inline-flex;
      align-items: center;
      font: var(--font-fg-12);

      .status-dot {
        width: 6px;
        height: 6px;
        border-radius: 50%;
        background: var(--black-40);
        margin-right: 4px;
        display: inline-block;
      }

      &.class-100 .status-dot { background: var(--green); }
      &.class-20 .status-dot { background: var(--yellow); }
      &.class-0 .status-dot { background: var(--black-40); }
    }

    .status-na {
      font: var(--font-fg-12);
      color: var(--black-40);
    }

    .badge {
      font-size: 10px;
      font-weight: 600;
      padding: 1px 6px;
      border-radius: 100px;

      &-featured {
        background: rgba(255, 165, 0, 0.12);
        color: #d97706;
      }

      &-removed {
        background: rgba(239, 68, 68, 0.1);
        color: #dc2626;
      }

      &-deleted {
        background: var(--black-10);
        color: var(--black-60);
      }
    }
  }

  .updates-cell {
    .update-row {
      font: var(--font-fg-12);
      color: var(--black-90);
      line-height: 1.6;

      .label {
        color: var(--black-60);
      }
    }
  }
}
</style>
